import React from 'react';
import { TabList, TabContext, TabPanel } from '@mui/lab';
import { Tab, styled, Box, Stack, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

const StyledTabList = styled(TabList, {
  shouldForwardProp: (prop) => {
    // Don't forward custom props to the DOM
    return prop !== 'variant' && prop !== 'color';
  },
})(({ theme, variant, color }) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'elevated':
        return {
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
          boxShadow: `0 4px 20px ${alpha(theme.palette.grey[500], 0.1)}, 0 0 0 1px ${alpha(theme.palette.grey[300], 0.2)}`,
          border: 'none',
        };
      case 'outlined':
        return {
          background: 'transparent',
          boxShadow: 'none',
          border: `2px solid ${theme.palette.primary.light}`,
        };
      case 'filled':
        return {
          background: `linear-gradient(135deg, ${theme.palette.primary.lighter} 0%, ${theme.palette.primary.light} 100%)`,
          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
          border: `1px solid ${theme.palette.primary.light}`,
        };
      default: // modern
        return {
          background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,
          boxShadow: `0 2px 8px ${alpha(theme.palette.grey[200], 0.4)}`,
          border: `1px solid ${theme.palette.grey[200]}`,
        };
    }
  };

  return {
    width: '100%',
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(0.75),
    ...getVariantStyles(),
    [theme.breakpoints.down('sm')]: {
      overflow: 'visible',
      '& .MuiTabs-scroller': {
        overflow: 'visible !important',
      },
    },
    '& .MuiTabs-flexContainer': {
      justifyContent: 'space-between',
      [theme.breakpoints.down('sm')]: {
        justifyContent: 'space-between',
        display: 'flex',
        width: '100%',
      },
    },
    '& .MuiTab-root': {
      width: '100%',
      minHeight: 48,
      borderRadius: theme.shape.borderRadius * 1.5,
      margin: theme.spacing(0.25),
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      overflow: 'hidden',
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      hyphens: 'auto',
      lineHeight: 1.2,
      padding: theme.spacing(1, 0.5),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      [theme.breakpoints.down('sm')]: {
        flex: 1,
        minWidth: 0,
        maxWidth: 'none',
        padding: theme.spacing(1, 0.75),
      },
    },
  };
});

const StyledTab = styled(Tab, {
  shouldForwardProp: (prop) => {
    // Don't forward custom props to the DOM
    return prop !== 'color' && prop !== 'glow';
  },
})(({ theme, color, glow }) => {
  const getColorPalette = () => {
    switch (color) {
      case 'success':
        return {
          main: theme.palette.success.main,
          dark: theme.palette.success.dark,
          light: theme.palette.success.light,
        };
      case 'warning':
        return {
          main: theme.palette.warning.main,
          dark: theme.palette.warning.dark,
          light: theme.palette.warning.light,
        };
      case 'error':
        return {
          main: theme.palette.error.main,
          dark: theme.palette.error.dark,
          light: theme.palette.error.light,
        };
      case 'info':
        return {
          main: theme.palette.info.main,
          dark: theme.palette.info.dark,
          light: theme.palette.info.light,
        };
      default:
        return {
          main: theme.palette.primary.main,
          dark: theme.palette.primary.dark,
          light: theme.palette.primary.light,
        };
    }
  };

  const colors = getColorPalette();

  return {
    color: theme.palette.text.secondary,
    backgroundColor: 'transparent',
    borderRadius: theme.shape.borderRadius * 1.5,
    fontWeight: 700,
    fontSize: '0.875rem',
    letterSpacing: '0.5px',
    textTransform: 'none',
    minHeight: 48,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    hyphens: 'auto',
    lineHeight: 1.2,
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
      transition: 'left 0.5s ease-in-out',
    },
    '&:hover': {
      backgroundColor: `linear-gradient(135deg, ${alpha(colors.main, 0.1)} 0%, ${alpha(colors.light, 0.05)} 100%)`,
      color: colors.main,
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 12px ${alpha(colors.main, 0.2)}`,
      '&:before': {
        left: '100%',
      },
    },
    '&.Mui-selected': {
      color: colors.main,
      background: `linear-gradient(135deg, ${alpha(colors.main, 0.08)} 0%, ${alpha(colors.light, 0.12)} 100%)`,
      boxShadow: glow 
        ? `0 2px 8px ${alpha(colors.main, 0.2)}, 0 0 0 1px ${alpha(colors.main, 0.15)}`
        : `0 2px 6px ${alpha(colors.main, 0.15)}, 0 0 0 1px ${alpha(colors.main, 0.1)}`,
      transform: 'translateY(-1px)',
      fontWeight: 700,
      '&:hover': {
        background: `linear-gradient(135deg, ${alpha(colors.main, 0.12)} 0%, ${alpha(colors.light, 0.16)} 100%)`,
        boxShadow: glow 
          ? `0 4px 12px ${alpha(colors.main, 0.25)}, 0 0 0 1px ${alpha(colors.main, 0.2)}`
          : `0 3px 10px ${alpha(colors.main, 0.2)}, 0 0 0 1px ${alpha(colors.main, 0.15)}`,
        transform: 'translateY(-2px)',
      },
      '&:before': {
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
      },
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  };
});

// ----------------------------------------------------------------------

const EnhancedTabs = ({ 
  children,
  value,
  onChange,
  variant = 'modern',
  color = 'primary',
  glow = false,
  centered = true,
  sx,
  ...props 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Filter out custom variant to prevent passing invalid variant to TabList
  // The variant is only used for styling in StyledTabList
  const tabListProps = { ...props };
  // Remove any variant prop that might be in props to avoid conflicts
  delete tabListProps.variant;
  
  // Use standard variant so tabs distribute evenly
  const muiVariant = 'standard';
  
  return (
    <StyledTabList
      value={value}
      onChange={onChange}
      variant={muiVariant}
      color={color}
      centered={centered}
      sx={sx}
      {...tabListProps}
    >
      {children}
    </StyledTabList>
  );
};

const EnhancedTab = ({ 
  children,
  color = 'primary',
  glow = false,
  ...props 
}) => {
  return (
    <StyledTab
      color={color}
      glow={glow}
      {...props}
    >
      {children}
    </StyledTab>
  );
};

// ----------------------------------------------------------------------

const EnhancedTabContext = ({ children, value, ...props }) => {
  return (
    <TabContext value={value} {...props}>
      {children}
    </TabContext>
  );
};

const EnhancedTabPanel = ({ children, value, ...props }) => {
  return (
    <TabPanel value={value} {...props}>
      {children}
    </TabPanel>
  );
};

export { 
  EnhancedTabs, 
  EnhancedTab, 
  EnhancedTabContext, 
  EnhancedTabPanel 
};
